import { ProductLinkType, } from '@as-react/common-types';
import { mergeProductPricesToProductLister } from '@as-react/common-utils';
import { all, call, fork, getContext, join, put, select, takeLatest } from 'redux-saga/effects';
import DeliveryPromiseAPI from '../../../api/DeliveryPromiseAPI';
import ProductDetailAPI from '../../../api/ProductDetailAPI';
import ProductsAPI from '../../../api/ProductsAPI';
import PromotionAPI from '../../../api/PromotionAPI';
import SearchAPI from '../../../api/search/SearchAPI';
import { FILTER_SIZE_SEPARATOR } from '../../../constants/GeneralConstants';
import { ProductActionTypes } from '../../../enums/ActionEnums';
import { getBundleChildrenPrice, getMonetateProductBundle, getBundlePrice } from '../../../util/ProductBundleUtil';
import { getQuerystringByName } from '../../../util/URLParamUtil';
import { analyticsActions } from '../../analytics/actions/AnalyticsActions.creators';
import { fetchMonetateProducts } from '../../monetate/sagas/MonetateSagas';
import { replaceRouterState } from '../../router/sagas/RouterSagas';
import { productActions } from '../actions/ProductActions';
import { makeGetBundle, makeGetDeliveryPromises, makeGetIsSelectedSizeSoldOut, makeGetPriceInformation, makeGetSelectedBundleProducts, makeGetSelectedSku, } from '../selectors/ProductSelector';
const PRODUCT_SIZE_PARAM_KEY = 'productSize';
export function* updateProductColorSaga() {
    yield takeLatest(ProductActionTypes.SET_COLOR, updateProductColor);
    yield takeLatest(ProductActionTypes.SET_ESI_DATA, checkForDeliveryPromiseOnEsiData);
}
function* loadProductDataSaga() {
    yield takeLatest(ProductActionTypes.LOAD_PRODUCT_DATA, clearProduct);
    yield takeLatest(ProductActionTypes.LOAD_PRODUCT_DATA, loadNewProduct);
}
function* clearProduct() {
    yield put(productActions.createClearProductData());
}
export const getPartialRootStateData = (state) => ({
    product: state.product,
});
export const getProductData = (state) => state.product;
export const getSelectedSku = (state) => state.product.selectedSku;
export const getProductId = (state) => state.product.productId;
export const getSelectedColourId = (state) => state.product.selectedColorId;
export const getProductColorVariations = (state) => state.product.productColorVariations;
export const getMainWebshopId = (state) => state.product.mainWebshopId;
const getSelectedProductSize = (state) => {
    var _a, _b, _c;
    if (!((_a = state.router.location) === null || _a === void 0 ? void 0 : _a.search))
        return null;
    return ((_c = (_b = getQuerystringByName(state.router.location.search, PRODUCT_SIZE_PARAM_KEY)) === null || _b === void 0 ? void 0 : _b.split(FILTER_SIZE_SEPARATOR)[0]) !== null && _c !== void 0 ? _c : null);
};
export function* updateProductColor(action) {
    const aemConfigurationContext = yield getContext('aemConfiguration');
    if (aemConfigurationContext.pageInfo.isPDPPage) {
        const partialRootState = yield select(getPartialRootStateData);
        const replacingRouterStateTask = yield fork(replaceRouterState, { colour: action.payload }, Object.assign({}, partialRootState));
        yield join(replacingRouterStateTask);
    }
}
export function* checkForDeliveryPromiseOnEsiData() {
    const selectedSku = yield select(getSelectedSku);
    const deliveryPromise = yield select(makeGetDeliveryPromises());
    const isSelectedSizeSoldOut = yield select(makeGetIsSelectedSizeSoldOut());
    if (selectedSku && !deliveryPromise && !isSelectedSizeSoldOut) {
        yield put(productActions.createSelectSizeAction({ sku: selectedSku }));
    }
}
function* loadNewProduct(action) {
    const aemConfigurationContext = yield getContext('aemConfiguration');
    const product = yield call(ProductDetailAPI.fetchProductForPromoBasketPopup, aemConfigurationContext.servicesApi, aemConfigurationContext.servicesApiPrefixAemServices, aemConfigurationContext.mainWebShop, aemConfigurationContext.fictiveWebShop, aemConfigurationContext.lang, aemConfigurationContext.isSingleLanguageSite, aemConfigurationContext.brandImageCdnUrl, action.payload.data.productCode);
    yield put(productActions.createSetNewProduct(product));
}
function* getTechnicalCategoriesSaga() {
    yield takeLatest(ProductActionTypes.GET_TECHNICAL_CATEGORIES, getTechnicalCategories);
}
function* getTechnicalCategories(action) {
    var _a;
    const { defaultRequestParams, servicesEndpoint } = yield getContext('aemConfiguration');
    const productId = yield select(getProductId);
    const productColorVariations = yield select(getProductColorVariations);
    const mainWebshopId = yield select(getMainWebshopId);
    const productColorId = (_a = productColorVariations === null || productColorVariations === void 0 ? void 0 : productColorVariations.find(colorVariation => colorVariation.colorId === action.payload)) === null || _a === void 0 ? void 0 : _a.productColorId;
    try {
        const technicalCategories = yield call(ProductDetailAPI.getTechnicalCategories, servicesEndpoint, defaultRequestParams, mainWebshopId, productId, productColorId, true);
        yield put(productActions.createSetTechnicalCategories(technicalCategories));
    }
    catch (_b) { }
}
function* getLinkedProductsSaga() {
    yield takeLatest(ProductActionTypes.GET_LINKED_PRODUCTS, getLinkedProducts);
}
function* getLinkedProducts(action) {
    var _a;
    const { defaultRequestParams, servicesEndpoint } = yield getContext('aemConfiguration');
    const productId = yield select(getProductId);
    const mainWebshopId = yield select(getMainWebshopId);
    const productColorVariations = yield select(getProductColorVariations);
    const productColorId = (_a = productColorVariations === null || productColorVariations === void 0 ? void 0 : productColorVariations.find(colorVariation => colorVariation.colorId === action.payload)) === null || _a === void 0 ? void 0 : _a.productColorId;
    try {
        const linkedProducts = yield call(ProductsAPI.getLinkedProducts, Number(productId), productColorId, ProductLinkType.STL, mainWebshopId, servicesEndpoint, defaultRequestParams);
        const products = yield all(linkedProducts.map(product => call(ProductsAPI.getSingleProductData, servicesEndpoint, defaultRequestParams.mainWebshop, defaultRequestParams.anaLang, defaultRequestParams.language, defaultRequestParams.shopId, product.linkedProductCode, product.linkedColourId.toString(), undefined, true)));
        const productsPriceData = yield ProductDetailAPI.getAllProductDetailPriceByProductId(products.map(product => product.productId), servicesEndpoint, defaultRequestParams);
        const mergedProducts = products.map((product, i) => mergeProductPricesToProductLister(product, productsPriceData[i]));
        yield put(productActions.createSetLinkedProducts(mergedProducts));
    }
    catch (_b) { }
}
function* fetchBundleProducts() {
    const { defaultRequestParams, servicesEndpoint } = yield getContext('aemConfiguration');
    const productId = yield select(getProductId);
    const colourId = yield select(getSelectedColourId);
    const bundle = yield call(PromotionAPI.getProductBundle, servicesEndpoint, defaultRequestParams, productId, colourId);
    return bundle;
}
function* getBundle({ payload: monetateId }) {
    try {
        const colourId = yield select(getSelectedColourId);
        let bundle = null;
        if (monetateId) {
            const parentProduct = yield select(getProductData);
            const monetateProducts = yield call(fetchMonetateProducts, {
                actionId: monetateId,
            });
            if (monetateProducts.length) {
                bundle = getMonetateProductBundle(parentProduct, monetateProducts, Number(monetateId));
            }
        }
        else {
            bundle = yield call(fetchBundleProducts);
        }
        if (bundle) {
            yield put(productActions.setBundle(bundle));
            yield put(analyticsActions.bundleDealsViewed(colourId));
        }
    }
    catch (_a) { }
}
function* getBundleSaga() {
    yield takeLatest(ProductActionTypes.GET_BUNDLE, getBundle);
}
function* calculateBundle() {
    var _a;
    const { defaultRequestParams, servicesEndpoint, businessUnitId } = yield getContext('aemConfiguration');
    const bundle = yield select(makeGetBundle());
    const selectedSku = yield select(makeGetSelectedSku());
    const selectedBundleProducts = yield select(makeGetSelectedBundleProducts());
    let calculation;
    if (!bundle || selectedBundleProducts.length === 0) {
        yield put(productActions.bundleCalculateFailure(new Error('No Bundle or no bundle products selected')));
        return;
    }
    try {
        if (bundle.isMonetateBundle) {
            const parentProductPrice = yield select(makeGetPriceInformation());
            const childrenPrices = getBundleChildrenPrice(bundle);
            calculation = {
                bundleCalculationPrice: getBundlePrice(parentProductPrice.prices, childrenPrices),
                bundleCalculationPromise: null,
            };
            if (selectedSku) {
                const selectedProducts = selectedBundleProducts.map(({ productId, colourId, skuId }) => ({
                    productId,
                    colorId: colourId,
                    skuId: Number(skuId),
                    quantity: 1,
                }));
                selectedProducts.push({
                    productId: bundle.productId,
                    colorId: bundle.colourId,
                    skuId: selectedSku,
                    quantity: 1,
                });
                try {
                    const bundleDeliveryPromise = yield call(DeliveryPromiseAPI.getBundleDeliveryPromise, servicesEndpoint, defaultRequestParams, businessUnitId, selectedProducts);
                    calculation.bundleCalculationPromise = Object.assign(Object.assign({}, bundleDeliveryPromise), { cutoff: ((_a = bundleDeliveryPromise.nextDayResult) === null || _a === void 0 ? void 0 : _a.cutoffAsUTC) || undefined });
                }
                catch (_b) { }
            }
        }
        else {
            calculation = yield call(PromotionAPI.calculateProductBundle, servicesEndpoint, defaultRequestParams, {
                bundleId: bundle.bundleId,
                productId: bundle.productId,
                colourId: bundle.colourId,
                selectedBundleProducts,
                skuId: selectedSku,
            });
        }
        yield put(productActions.bundleCalculateSuccess(calculation));
    }
    catch (error) {
        yield put(productActions.bundleCalculateFailure(error));
    }
}
function* calculateBundleSaga() {
    yield takeLatest(ProductActionTypes.BUNDLE_CALCULATE_START, calculateBundle);
}
export function* handleProductDetailInitSaga() {
    yield takeLatest(ProductActionTypes.INIT, handleProductDetailInit);
}
function* handleProductDetailInit() {
    /**
     * Handle product sizes
     */
    const selectedProductSize = yield select(getSelectedProductSize);
    const productInfo = yield select(getProductData);
    if (productInfo.productColorVariations && productInfo.selectedColorId && selectedProductSize) {
        const selectedProductColorVariations = productInfo.productColorVariations.filter(productColorVariation => productColorVariation.colorId === productInfo.selectedColorId);
        if (selectedProductColorVariations && selectedProductColorVariations.length > 0) {
            const selectedProductColorVariation = selectedProductColorVariations[0];
            const availableSizes = selectedProductColorVariation.sizes.filter(s => s.availability && s.availability > 0);
            const matchingOriginalSize = availableSizes.find(s => {
                var _a, _b, _c, _d;
                return ((_a = s.fullCommercialSize) === null || _a === void 0 ? void 0 : _a.startsWith(selectedProductSize)) ||
                    ((_b = s.fullCommercialSize) === null || _b === void 0 ? void 0 : _b.split(' ')[1]) === selectedProductSize ||
                    ((_c = s.fullCommercialSize) === null || _c === void 0 ? void 0 : _c.split(' | ')[1]) === selectedProductSize ||
                    ((_d = s.originalSize) === null || _d === void 0 ? void 0 : _d.split('/').includes(selectedProductSize)) ||
                    s.originalSize === selectedProductSize ||
                    s.code === selectedProductSize;
            });
            if (matchingOriginalSize) {
                yield put(productActions.createSelectSizeAction({ sku: matchingOriginalSize.sku }));
            }
        }
    }
    /**
     * Product availability
     */
    const aemConfigurationContext = yield getContext('aemConfiguration');
    const productAvailability = yield call(ProductDetailAPI.getProductDetailAvailabilityByProductId, aemConfigurationContext.servicesApi, aemConfigurationContext.mainWebShop, productInfo.productId);
    yield put(productActions.createSetEsiDataAction({ productAvailability }));
}
export function* fetchAlternativesSaga() {
    yield takeLatest(ProductActionTypes.FETCH_ALTERNATIVES, fetchAlternatives);
}
export function* fetchAlternatives(action) {
    const { defaultRequestParams, servicesEndpoint } = yield getContext('aemConfiguration');
    const productId = yield select(getProductId);
    const colourId = yield select(getSelectedColourId);
    try {
        const searchResults = yield call(SearchAPI.searchFredhopperProducts, servicesEndpoint, defaultRequestParams, productId, colourId);
        const alternativeProducts = searchResults.find((item) => item.campaignName === action.payload.campaign || item.campaignName === action.payload.campaignFallback);
        yield put(productActions.setAlternatives((alternativeProducts === null || alternativeProducts === void 0 ? void 0 : alternativeProducts.items) || []));
        yield put(productActions.setAlternativesCampaign({ campaignId: alternativeProducts === null || alternativeProducts === void 0 ? void 0 : alternativeProducts.campaignName }));
    }
    catch (_a) { }
}
export default function* productSaga() {
    yield all([
        updateProductColorSaga(),
        handleProductDetailInitSaga(),
        loadProductDataSaga(),
        getTechnicalCategoriesSaga(),
        getLinkedProductsSaga(),
        getBundleSaga(),
        calculateBundleSaga(),
        fetchAlternativesSaga(),
    ]);
}
